import _ from "lodash";
import { defineStore } from "pinia";
import { useUserStore } from "./UserStore";
import { useObserverStore } from "./ObserverStore";
import { useTalkRoomTmpViewStore } from "~/stores/TalkRoomTmpViewStore";
import { useTalkUnreadStore } from "~/stores/TalkUnreadStore";
import { TalkRoom } from "~/entities/TalkRoom";
import { DeleteType, newLoadingTalk, Talk } from "~/entities/Talk";
import { TalkOgp } from "~/entities/TalkOgp";
import { TalkRoomUser } from "~/entities/TalkRoomUser";
import type { ITalkRoomRepository, ObservedTalkRoom } from "~/interfaces/ITalkRoomRepository";
import { TalkRoomRepository } from "~/repositories/TalkRoomRepository";
import { OgpRepository } from "~/repositories/OgpRepository";
import { useLoginStore } from "~/stores/LoginStore";
import { usePlanStore } from "~/stores/PlanStore";
import { useErrorStore } from "~/stores/ErrorStore";
import {
  TALK_ROOM_GROUP_MAX,
  TALK_ROOM_DM_MAX,
  TALK_ROOM_DM_INIT_UNREAD_MAX,
  TALK_MENTION_USERS_MAX,
  TALK_THREAD_ID_NONE,
  TALK_DATE_FROM_NONE,
  TALK_DATE_FROM_ALL,
} from "~/consts/TalkConfig";

/**
 * トークルームデータを扱うクラス
 * getTalkでユーザーデータを利用するので、トークルーム変更時には最初にgetUsersを呼ぶこと。
 */
export const useTalkRoomStore = defineStore("TalkRoomStore", () => {
  // 定数
  const ENABLE_PREFETCH = true;
  const USE_CACHE_FOR_ASYNC_DATA = true; // デバッグ時のみ false にしてキャッシュ無効化する
  const DELAY_ASYNC_SEC = 0; // デバッグ時のみ 1 以上にして非同期処理を遅らせる

  // グローバルデータ
  const talkOgpLists = ref<{ [talkId: string]: TalkOgp[] }>({}); // OGP情報のキャッシュ
  const replyTalks = ref<Talk[]>([]); // 返信用のトークのキャッシュ。talksに含まれていない場合はこちらから取得する
  const threadSrcTalks = ref<Talk[]>([]); // スレッド用のトークのキャッシュ。talksに含まれていない場合はこちらから取得する

  // 講座単位データ
  const selectedPlanId = ref<string>("");
  const planOwners = ref<TalkRoomUser[]>([]);
  const talkRooms = ref<TalkRoom[]>([]);
  const talkRoomsForSetting = ref<TalkRoom[]>([]); // 通知設定用のトークルームリストを別でもつ
  const observeTalkRooms = ref<ObservedTalkRoom[]>([]);

  // トークルーム単位データ
  const selectedTalkRoomId = ref<string>("");
  const talkRoomSetDone = ref<boolean>(false);
  const talks = ref<Talk[]>([]);
  const prefetchedTalksBefore = ref<Talk[]>([]);
  const prefetchedTalksAfter = ref<Talk[]>([]);
  const replyTalkToEdit = ref<Talk | null>(null); // 編集中の返信トーク
  const threadTalks = ref<Talk[]>([]);
  const talkRoomUsers = ref<{ [id: string]: TalkRoomUser }>({});
  const userGoodList = ref<{ [id: string]: string[] }>({});
  const hasPrevTalk = ref<boolean>(false);
  const hasNextTalk = ref<boolean>(false);
  const loading = ref(false);

  // talks, threadTalks の length は監視できるが中身の変更は監視できないのでフラグを立てる
  const updateTalk = ref(false);
  const updateThreadTalk = ref(false);

  const repository: ITalkRoomRepository = new TalkRoomRepository();
  const ogpRepository = new OgpRepository();

  const loginStore = useLoginStore();
  const planStore = usePlanStore();
  const userStore = useUserStore();
  const errorStore = useErrorStore();
  const observerStore = useObserverStore();
  const talkRoomTmpViewStore = useTalkRoomTmpViewStore();
  const talkUnreadStore = useTalkUnreadStore();

  const ogpRequestCached = new Map(); // URLごとのブラウザキャッシュ有無
  const ogpRequestDebounces = new Map(); // トークxURLごとのデバウンス
  const replyRequestDebounces = new Map(); // トークごとのデバウンス
  const threadRequestDebounces = new Map(); // トークごとのデバウンス

  const delayAsyncForDebug = async () => {
    if (DELAY_ASYNC_SEC > 0) {
      await new Promise(resolve => setTimeout(resolve, DELAY_ASYNC_SEC * 1000));
    }
  };

  // トーク監視状態
  let talksObserveInitiated = false;
  let talksObserveFrom: string | undefined;
  let talksObserveTo: string | undefined;

  // 講座初期化
  const initPlan = (planId: string) => {
    // 講座が変わった場合のみ講師リストを初期化
    if (selectedPlanId.value !== planId) {
      planOwners.value = [];
    }

    // 一時ビュー状態初期化
    talkRoomTmpViewStore.initPlan();

    selectedPlanId.value = planId;
  };

  // トークルーム初期化
  const initTalkRoom = (talkRoomId: string) => {
    selectedTalkRoomId.value = talkRoomId;
    talkRoomSetDone.value = false;

    // 一時ビュー状態初期化
    talkRoomTmpViewStore.initTalkRoom();

    ogpRequestDebounces.clear();
    replyRequestDebounces.clear();
    threadRequestDebounces.clear();

    resetTalks();
    initTalksObserve();
  };

  // トークデータ初期化
  const resetTalks = () => {
    hasPrevTalk.value = false;
    hasNextTalk.value = false;
    talks.value = [];
    updateTalk.value = false;
    prefetchedTalksBefore.value = [];
    prefetchedTalksAfter.value = [];
  };

  // トーク開始日付取得
  const getTalkDateFrom = (talkRoomId: string) => {
    const planInfo = planStore.plan!;
    const talkRoom = talkRooms.value.find(room => room.talkRoomId === talkRoomId);

    if (!talkRoom) {
      sentryErrorLog(`getTalkDateFrom: talkRoom not found for talkRoomId: ${talkRoomId}`, {
        talkRoomId,
        availableTalkRoomIds: talkRooms.value.map(r => r.talkRoomId),
      });
      return TALK_DATE_FROM_NONE;
    }

    if (!talkRoom.blockPastTalks) {
      // 過去トークをブロックしない場合は全データアクセス可能
      return TALK_DATE_FROM_ALL;
    } else if (planInfo.isOwner) {
      // 講師は全データアクセス可能
      return TALK_DATE_FROM_ALL;
    } else if (isContractPlan(planInfo)) {
      // 入会者は契約開始日からアクセス可能
      const d = new Date(planInfo.contractStatus.startDate);
      // 日本時間 00:00:00 基準にするため、UTC-9 に設定
      // https://arkad-co.slack.com/archives/C06P8P9L95G/p1758161777655929?thread_ts=1758161140.116019&cid=C06P8P9L95G
      d.setUTCHours(-9, 0, 0, 0);
      return d;
    }
    // 未入会者はアクセス不可
    return TALK_DATE_FROM_NONE;
  };

  // トーク有効チェック
  const isTalkValid = (talkRoomId: string, postDateTime: Date) => {
    const dateFrom = getTalkDateFrom(talkRoomId);
    return postDateTime >= dateFrom;
  };
  const validateTalk = (talk: Talk) => {
    if (!talk.talkRoomId) {
      throw new Error("validateTalk: Invalid talk: missing talkRoomId");
    }
    if (!isTalkValid(talk.talkRoomId, talk.postDateTime!)) {
      // 入会前の投稿など、無効なトークは閲覧・ジャンプできないようにシステム削除扱いとする
      talk.deleteType = DeleteType.systemDelete;
    }
  };

  // トークルームおよびホームタブへ未読フラグを設定する
  const setTalkRoomUnread = (talkRoomId: string, unreadFlag: boolean) => {
    const talkRoom = talkRooms.value.find(item => item.talkRoomId === talkRoomId);
    if (!talkRoom || !planStore.plan) {
      return;
    }
    // トークルームの未読フラグを設定する
    talkRoom.hasUnread = unreadFlag;
    // ホームタブの未読フラグも設定する
    talkUnreadStore.setUnread("home", talkRoomId, unreadFlag);
  };

  // トークルームの投稿日時を更新
  const setTalkRoomPostDateTime = (talkRoomId: string, postDateTime: string) => {
    const talkRoom = talkRooms.value.find(item => item.talkRoomId === talkRoomId);
    if (!talkRoom || !planStore.plan) {
      return;
    }
    // トークルームの投稿日時を更新する
    talkRoom.latestPostDateTime = postDateTime;
  };

  // トークルームのデータ監視を行う。ここではlatesteTalkIDの監視のみでトークデータの監視はしない。
  const startTalkRoomsObserve = (planId: string) => {
    observeTalkRooms.value = [];

    observerStore.add("talkRooms", {
      start: () => repository.startTalkRoomsObserve(planId, addTalkRoomCallback, changeTalkRoomCallback),
      stop: () => repository.stopTalkRoomsObserve(),
    });
  };

  const stopTalkRoomsObserve = () => {
    try {
      observerStore.remove("talkRooms");
    } finally {
      clearTalkRoom();
    }
  };

  const clearTalkRoom = () => {
    talkRooms.value = [];
  };

  const startUsersObserve = (planId: string) => {
    observerStore.add("talkUsers", {
      start: () => repository.startUsersObserve(planId, addUsersCallback, changeUsersCallback),
      stop: () => repository.stopUsersObserve(),
    });
  };

  const stopUsersObserve = () => {
    observerStore.remove("talkUsers");
  };

  // トークデータ監視初期化
  const initTalksObserve = () => {
    talksObserveInitiated = false;
    talksObserveFrom = undefined;
    talksObserveTo = undefined;
  };

  // トークデータの監視を行う
  const startTalksObserve = (planId: string, talkRoomId: string, fromTalkId: string, toTalkId: string) => {
    // トークデータ監視は重いので、条件が同じ場合は監視しないようにチェックを入れる
    if (talksObserveInitiated && talksObserveFrom === fromTalkId && talksObserveTo === toTalkId) {
      // 2回目以降かつ前回と同じ監視条件の場合は監視しない
      return;
    }
    // 監視状態を設定
    talksObserveInitiated = true;
    talksObserveFrom = fromTalkId;
    talksObserveTo = toTalkId;

    observerStore.add("talks", {
      start: () =>
        repository.startTalksObserve(
          planId,
          talkRoomId,
          getTalkDateFrom(talkRoomId),
          addCallback,
          changeCallback,
          fromTalkId,
          toTalkId,
          () => {
            talkRoomSetDone.value = true;
          },
        ),
      stop: () => repository.stopTalksObserve(),
    });
  };

  const stopTalksObserve = () => {
    observerStore.remove("talks");
  };

  const startThreadTalksObserve = (planId: string, talkRoomId: string, threadTalkId: string) => {
    observerStore.add("threadTalks", {
      start: () =>
        repository.startThreadTalksObserve(
          planId,
          talkRoomId,
          threadTalkId,
          getTalkDateFrom(talkRoomId),
          async (talk: Talk) => {
            await addThreadCallback(planId, talk);
          },
          async (talk: Talk) => {
            await changeThreadCallback(planId, talk);
          },
        ),
      stop: () => repository.stopThreadTalksObserve(),
    });
  };

  const stopThreadTalksObserve = () => {
    observerStore.remove("threadTalks");
  };

  const addThreadCallback = async (planId: string, talk: Talk) => {
    if (talk.talkRoomId !== selectedTalkRoomId.value) {
      // 選択中のトークルームではない場合は何もしない
      return;
    }

    // スレッド追加時
    const idx = threadTalks.value.findIndex(item => item.talkId === talk.talkId);
    if (idx === -1) {
      // ユーザー情報を取得（スレッドへトーク設定する前に）
      await updateTalkRoomUsers(planId, [talk]);
      // スレッドへトークを追加（順序保証のためソート）
      const newThreadTalks = [...threadTalks.value];
      newThreadTalks.push(talk);
      newThreadTalks.sort((a, b) => Number(a.talkId) - Number(b.talkId));
      threadTalks.value = newThreadTalks;
      setThreadTalkInfo(talk); // OGP・ブックマークなどを非同期で付与
    }
  };

  const changeThreadCallback = async (planId: string, talk: Talk) => {
    if (talk.talkRoomId !== selectedTalkRoomId.value) {
      // 選択中のトークルームではない場合は何もしない
      return;
    }

    // スレッド更新・論理削除時
    const idx = threadTalks.value.findIndex(item => item.talkId === talk.talkId);
    if (idx >= 0) {
      // ユーザー情報を取得（スレッドへトーク設定する前に）
      await updateTalkRoomUsers(planId, [talk]);
      // スレッドのトークを更新
      threadTalks.value[idx] = talk;
      setThreadTalkInfo(talk); // OGP・ブックマークなどを非同期で付与
    }
  };

  // トークルームにてユーザーのいいね済みリスト監視を行う
  const startUserGoodsObserve = (talkRoomId: string) => {
    if (!loginStore.isLogin()) return; // ログインしていない場合は何もしない

    // FINCS-2197対応: talkRoomIdのバリデーション
    if (!talkRoomId || talkRoomId.trim() === "") {
      sentryErrorLog("startUserGoodsObserve: talkRoomId is required");
      return;
    }

    // FINCS-2197対応: userIdのバリデーション（isLogin()を通り抜けているがuserIdが空の場合があるため再チェック）
    const userId = loginStore.loginInfo?.userId;
    if (!userId || userId.trim() === "") {
      sentryErrorLog("startUserGoodsObserve: userId is required (loginInfo may be in inconsistent state)");
      return;
    }

    observerStore.add("talkUserGoods", {
      start: () =>
        repository.startUserGoodsObserve(userId, talkRoomId, (type: string, id: string) => {
          if (!userGoodList.value[talkRoomId]) {
            userGoodList.value[talkRoomId] = [];
          }
          if (type === "added") {
            userGoodList.value[talkRoomId].push(id);
          } else if (type === "removed") {
            userGoodList.value[talkRoomId] = userGoodList.value[talkRoomId].filter(item => item !== id);
          }
        }),
      stop: () => repository.stopUserGoodsObserve(),
    });
  };

  const stopUserGoodsObserve = () => {
    observerStore.remove("talkUserGoods");
  };

  const setUpdateTalkFlag = () => {
    updateTalk.value = true;
  };

  const clearUpdateTalkFlag = () => {
    updateTalk.value = false;
  };

  const setUpdateThreadTalkFlag = () => {
    updateThreadTalk.value = true;
  };

  const clearUpdateThreadTalkFlag = () => {
    updateThreadTalk.value = false;
  };

  const updateTalkValue = (talkId: string, updateFunc: (talk: Talk) => void) => {
    const talk = talks.value.find(item => item.talkId === talkId);
    if (talk) {
      updateFunc(talk);
      setUpdateTalkFlag();
    }
    const threadTalk = threadTalks.value.find(item => item.talkId === talkId);
    if (threadTalk) {
      updateFunc(threadTalk);
      setUpdateThreadTalkFlag();
    }
  };

  const getDmRoomsToCheckUnread = (
    rooms: TalkRoom[],
    isOwner: boolean,
    isContract: boolean,
    dmLastReadAt: Date | undefined,
  ) => {
    return rooms
      .filter(room => room.isDirectMessage && !room.isArchive && canViewTalkRoom(room, isOwner, isContract))
      .filter(room => {
        // 講師の場合、最新投稿がDM配信であれば未読チェック対象外とする
        if (isOwner && room.latestIsDelivery) {
          return false;
        }
        return true;
      })
      .filter(room => {
        if (!dmLastReadAt) {
          // 最終既読日時が未設定の場合は全て未読チェック対象とする
          return true;
        }
        // DMルームは、最新投稿日時が最終既読時刻より後であれば未読チェックする
        const latestPostDateTime = new Date(room.latestPostDateTime);
        return dmLastReadAt.getTime() < latestPostDateTime.getTime();
      })
      .sort((a, b) => {
        // latestPostDateTime の降順でソートして TALK_ROOM_DM_INIT_UNREAD_MAX 件までを対象とする
        return new Date(b.latestPostDateTime).getTime() - new Date(a.latestPostDateTime).getTime();
      })
      .slice(0, TALK_ROOM_DM_INIT_UNREAD_MAX);
  };

  const hasUnreadTalk = async (planId: string) => {
    if (!loginStore.isLogin()) {
      return false; // 未ログイン時は未読フラグはない
    }

    const isOwner = !!planStore.plan?.isOwner;
    const isContract = isContractPlan(planStore.plan);
    const rooms = await repository.getTalkRooms(planId, false);

    // 未読チェック対象チャンネルリストを作成
    const channelRooms = rooms.filter(
      room => !room.isDirectMessage && !room.isArchive && canViewTalkRoom(room, isOwner, isContract),
    );

    // 未読チェック対象DMリストを作成
    const dmRooms = getDmRoomsToCheckUnread(rooms, isOwner, isContract, undefined);

    // チャンネルと DM ルームを結合して未読チェック対象リストを作成
    const roomsToCheckUnread = channelRooms.concat(dmRooms);

    // 未読チェック実行
    const unreadMap = await repository.getTalkRoomUnreads(
      loginStore.loginInfo.userId,
      planId,
      roomsToCheckUnread.map(t => t.talkRoomId),
      true, // 最初の未読が見つかったら終了
    );

    // 上記にて最初の未読でチェックをやめてしまうため、
    // getTalkRoomsのようにDMルーム最終既読日時更新処理は実行しない

    // ひとつでも未読なら true を返す
    return roomsToCheckUnread.some(t => unreadMap[t.talkRoomId] === true);
  };

  const getTalkRooms = async (planId: string, excludeDm = false, excludeUnreads = false) => {
    try {
      talkRooms.value = await repository.getTalkRooms(planId, excludeDm);

      // プラン制限で閲覧不可のトークルームを除外
      talkRooms.value = talkRooms.value.filter(room => canShowTalkRoom(room, planStore.plan ?? null));

      if (loginStore.isLogin() && !excludeUnreads) {
        // ログインしていて未読チェックを行う場合
        // https://fincs.backlog.com/view/FINCS-4187
        // より、対象を絞る
        const isOwner = !!planStore.plan?.isOwner;
        const isContract = isContractPlan(planStore.plan);

        // 未読チェック対象チャンネルリストを作成
        const channelRooms = talkRooms.value.filter(
          room => !room.isDirectMessage && !room.isArchive && canViewTalkRoom(room, isOwner, isContract),
        );

        // DMルームの最終既読時刻を取得
        const dmLastReadAt = await repository.getDmLastReadAt(loginStore.loginInfo.userId, planId);

        // 未読チェック対象DMリストを作成
        const dmRooms = getDmRoomsToCheckUnread(talkRooms.value, isOwner, isContract, dmLastReadAt);

        // チャンネルと DM ルームを結合して未読チェック対象リストを作成
        const roomsToCheckUnread = channelRooms.concat(dmRooms);

        // 未読チェック実行
        const unreadMap = await repository.getTalkRoomUnreads(
          loginStore.loginInfo.userId,
          planId,
          roomsToCheckUnread.map(t => t.talkRoomId),
          false,
        );

        // 各トークルームの未読フラグを設定
        roomsToCheckUnread.forEach(room => {
          setTalkRoomUnread(room.talkRoomId, unreadMap[room.talkRoomId] || false);
        });

        // DMルーム最終既読日時更新処理
        let newDmLastReadAt = dmLastReadAt;
        let unreadDmFound = false;
        // 最新投稿日時が古い順にソートしてから処理
        dmRooms.toReversed().forEach(room => {
          if (unreadMap[room.talkRoomId]) {
            // 未読があった場合
            unreadDmFound = true;
          } else if (!unreadDmFound) {
            // ここまですべて既読の場合
            const latestPostDateTime = new Date(room.latestPostDateTime);
            if (newDmLastReadAt.getTime() < latestPostDateTime.getTime()) {
              // DMルームの最終既読日時が新しい場合は更新候補とする
              newDmLastReadAt = latestPostDateTime;
            }
          }
        });
        if (dmLastReadAt.getTime() !== newDmLastReadAt.getTime()) {
          // DMルームの最終既読日時を適宜更新
          await repository.setDmLastReadAt(loginStore.loginInfo.userId, planId, newDmLastReadAt);
        }
      }
    } catch (error: any) {
      errorStore.showConnectionError(`TalkRoomStore.getTalkRooms: ${error}`);
    }
  };

  const getTalkRoomsForSetting = async (planId: string) => {
    try {
      // 通知設定では DM ルームの情報は不要なため、 excludeDm = true で固定
      talkRoomsForSetting.value = await repository.getTalkRooms(planId, true);

      // プラン制限で閲覧不可のトークルームを除外
      talkRoomsForSetting.value = talkRoomsForSetting.value.filter(room =>
        canShowTalkRoom(room, planStore.plan ?? null),
      );
    } catch (error: any) {
      errorStore.showConnectionError(`TalkRoomStore.getTalkRoomsForSetting: ${error}`);
    }
  };

  const getTalkRoomDetail = async (planId: string, talkRoomId: string): Promise<TalkRoom> => {
    try {
      return await repository.getTalkRoomDetail(planId, talkRoomId);
    } catch (error: any) {
      errorStore.showConnectionError(`TalkRoomStore.getTalkRoomDetail: ${error}`);
      throw error;
    }
  };

  const searchTalkCountsInPlan = async (planId: string, searchWord: string, countMax: number) => {
    const res = await repository.searchTalksByPlan(planId, searchWord, countMax);
    const talkCounts = new Array<{ talkRoomId: string; count: number }>();
    for (const talk of res) {
      // トークルームごとのカウンタを取得 or 作成
      let talkCount = talkCounts.find(item => item.talkRoomId === talk.talkRoomId);
      if (!talkCount) {
        talkCount = {
          talkRoomId: talk.talkRoomId,
          count: 0,
        };
        talkCounts.push(talkCount);
      }
      if (isTalkValid(talk.talkRoomId, talk.postDateTime!)) {
        // 有効なトークであればカウントアップ
        talkCount.count++;
      }
    }
    return talkCounts.filter(item => item.count > 0); // カウント0のものは除外
  };

  const searchTalkIds = async (talkRoomId: string, searchWord: string, countMax: number) => {
    const res = await repository.searchTalksByTalkRoom(talkRoomId, searchWord, countMax);
    return res.map(item => {
      if (item.threadTalkId !== TALK_THREAD_ID_NONE) {
        // トーク検索にてスレッド内コメントがヒットしたら親トークが jiggle アニメ
        // スレッド表示モーダル内検索はブラウザ検索を使ってもらう
        // https://arkad-co.slack.com/archives/C06P8P9L95G/p1747274349411249?thread_ts=1745892147.082609&cid=C06P8P9L95G
        return item.threadTalkId;
      }
      return item.talkId;
    });
  };

  const fetchTalkRoomUser = async (talk: Talk) => {
    await updateTalkRoomUsers(selectedPlanId.value, [talk]);
  };

  const getGoodUsers = async (talkId: string, page: number) => {
    return await repository.getGoodUsersFromApi(talkId, page);
  };

  const updateTalkRoomUsers = async (planId: string, talks: Talk[]) => {
    // 不正データを検出してエラーログを出力
    // https://fincs.backlog.com/view/FINCS-3234
    for (const talk of talks) {
      if (talk.mentionUserIds && talk.mentionUserIds.includes("")) {
        sentryErrorLog(
          `updateTalkRoomUsers: empty mentionUserId found: ${planId}/${selectedTalkRoomId.value}/${talk.talkId}`,
        );
      }
    }

    // 指定トークリストから未取得のユーザーIDをユニークに取得
    // - 投稿者ユーザーID
    const userIds = talks.map(t => t.userId);
    // - メンションユーザーID (上記3234の不正データ補正のためにfilter追加)
    const mentionUserIds = talks.flatMap(t => t.mentionUserIds || []).filter(id => id !== "");
    const userIdsToQueryTmp = [];
    for (const userId of userIds.concat(mentionUserIds)) {
      if (!(userId in talkRoomUsers.value)) {
        userIdsToQueryTmp.push(userId);
      }
    }
    if (userIdsToQueryTmp.length === 0) {
      return; // すべて取得済みなので何もしない
    }
    const userIdsToQuery = _.uniq(userIdsToQueryTmp);

    // まず Firestore からユーザー情報を取得
    const users = await repository.getUsers(planId, userIdsToQuery);
    if (users?.length) {
      for (const user of users) {
        talkRoomUsers.value[user.userId] = user;
      }
    }

    // Firestore から取得しきれなかったユーザーについては API から取得
    // TODO 性能懸念あり。firestore へのユーザー情報保存形式を要検討
    const remainingUserIds = userIdsToQuery.filter(
      userId =>
        // userName はメンションで必須なので、取得済みであっても userName がない場合は再取得
        !(userId in talkRoomUsers.value) || !talkRoomUsers.value[userId].userName,
    );
    if (remainingUserIds.length > 0) {
      for (const userId of remainingUserIds) {
        await userStore.fetchOther(userId, true);
        const user = userStore.otherUser;
        if (!user) {
          throw new Error(`updateTalkRoomUsers: failed to fetch ${userId}`);
        }
        // TODO userPlanBadges も設定する
        talkRoomUsers.value[user.userId] = new TalkRoomUser(user.userId, user.userName, user.userImage);
      }
    }
  };

  const getMentionMembers = async (searchTerm: string, count: number): Promise<TalkRoomUser[]> => {
    return await repository.getPlanMembers(selectedPlanId.value, searchTerm, 1, count);
  };

  const getMentionUsers = async (searchTerm: string): Promise<TalkRoomUser[]> => {
    if (!selectedPlanId.value || !selectedTalkRoomId.value) {
      return [];
    }

    // 対象トークルーム決定
    const talkRoom = talkRooms.value.find(t => t.talkRoomId === selectedTalkRoomId.value);
    if (!talkRoom) {
      return [];
    }

    // selectedTalkRoom.value.dmUser は常に存在するため、 userId でチェックする
    const dmUser = talkRoom.dmUser?.userId ? talkRoom.dmUser : undefined;

    // 非公開・アーカイブの場合は講師のみ
    // （そもそもそれであればチャンネル参照できないが、念のためガード）
    const onlyOwners = talkRoom.isArchive || !talkRoom.published;

    // 講師がなければ取得
    if (planOwners.value.length === 0) {
      planOwners.value = await repository.getPlanOwners(selectedPlanId.value);
    }
    // メンションユーザー最大数
    const maxUsers = TALK_MENTION_USERS_MAX;
    // 講師をフィルタリング
    const searchedOwners = searchTerm.length
      ? planOwners.value.filter(o => o.userName.includes(searchTerm))
      : planOwners.value;
    // 最後、自分を差し引く場合に備え 1 人分余裕を持たせる
    const maxMembers = maxUsers - searchedOwners.length + 1;

    // 講師限定であれば会員を取得しない
    // DM の場合はその DM ユーザーのみ取得
    // その他の場合は会員を最大件数取得
    const searchedMembers = onlyOwners ? [] : dmUser ? [dmUser] : await getMentionMembers(searchTerm, maxMembers);

    // 自分を除外したうえで上位を最大数分表示
    return [...searchedOwners, ...searchedMembers]
      .filter(u => loginStore.loginInfo.userId !== u.userId)
      .slice(0, maxUsers);
  };

  // トーク順序が正しいかチェック
  const checkTalksOrder = (talks: Talk[]) => {
    for (let i = 0; i < talks.length - 1; i++) {
      if (Number(talks[i].talkId) >= Number(talks[i + 1].talkId)) {
        return false;
      }
    }
    return true;
  };

  // トーク順序が正しいかチェックして、正しくない場合はソート
  // また重複があれば除去する
  const adjustTalksOrder = (talks: Talk[]) => {
    if (!checkTalksOrder(talks)) {
      // 正常系のスレッド内コメントのチャンネル表示ON/OFF時に発生する
      // デバッグ用にログ出力をコメントアウトで残す
      // consoleWarn(
      //   `adjustTalksOrder: talks order invalid: room:${selectedTalkRoomId.value}, talks:${talks.map(t => t.talkId)}`,
      // );

      // talkId でソート
      talks.sort((a, b) => {
        return Number(a.talkId) - Number(b.talkId);
      });
      // talkId での重複除去
      talks = _.uniqBy(talks, "talkId");
    }
    if (!checkTalksOrder(talks)) {
      sentryErrorLog(
        `adjustTalksOrder: talks order still invalid: room:${selectedTalkRoomId.value}, talks:${talks.map(t => t.talkId)}`,
      );
    }
    return talks;
  };

  // トークユーザーデータ取得済みかチェック
  const checkTalksUsers = (talks: Talk[]) => {
    for (const talk of talks) {
      if (!(talk.userId in talkRoomUsers.value)) {
        return false;
      }
    }
    return true;
  };

  // トークユーザーデータ取得済みかチェックして、なければ取得
  const adjustTalksUsers = async (talks: Talk[]) => {
    if (!checkTalksUsers(talks)) {
      consoleWarn(
        `adjustTalksUsers: talks users missing: room:${selectedTalkRoomId.value}, talks:${talks.map(t => t.talkId)}`,
      );
      for (const talk of talks) {
        if (!(talk.userId in talkRoomUsers.value)) {
          await fetchTalkRoomUser(talk);
        }
      }
    }
    if (!checkTalksUsers(talks)) {
      sentryErrorLog(
        `adjustTalksUsers: talks users still missing: room:${selectedTalkRoomId.value}, talks:${talks.map(t => t.talkId)}`,
      );
    }
  };

  const updateTalks = async (planId: string, newTalks: Talk[]) => {
    // 選択中トークルームのデータに限定
    newTalks = newTalks.filter(t => t.talkRoomId === selectedTalkRoomId.value);

    await updateTalkRoomUsers(planId, newTalks);

    // トークユーザーデータ取得済みかチェックして、なければ取得
    await adjustTalksUsers(newTalks);

    // 順序が崩れていた場合はソートしてから設定
    talks.value = adjustTalksOrder(newTalks);
  };

  const addOneTalk = async (planId: string, newTalk: Talk) => {
    await updateTalkRoomUsers(planId, [newTalk]);

    // トークユーザーデータ取得済みかチェックして、なければ取得
    await adjustTalksUsers([newTalk]);

    // 順序が崩れていた場合はソートしてから設定
    const talksToCheck = [...talks.value];
    talksToCheck.push(newTalk);
    if (checkTalksOrder(talksToCheck)) {
      talks.value.push(newTalk);
    } else {
      talks.value = adjustTalksOrder(talksToCheck);
    }
  };

  const updateOneTalk = async (planId: string, newTalk: Talk) => {
    await updateTalkRoomUsers(planId, [newTalk]);
    const oldIndex = talks.value.findIndex(item => item.talkId === newTalk.talkId);

    // トークユーザーデータ取得済みかチェックして、なければ取得
    await adjustTalksUsers([newTalk]);

    // 順序が崩れていた場合はソートしてから設定
    const talksToCheck = [...talks.value];
    talksToCheck[oldIndex] = newTalk;
    if (checkTalksOrder(talksToCheck)) {
      talks.value[oldIndex] = newTalk;
    } else {
      talks.value = adjustTalksOrder(talksToCheck);
    }
  };

  const hasTalk = async (planId: string, talkRoomId: string, talkId: string) => {
    return !!(await repository.getTalk(planId, talkRoomId, talkId));
  };

  const getTalksBefore = async (
    planId: string,
    talkRoomId: string,
    talkId: string | undefined,
    pageSize = 20,
    useCacheIfAvailable: boolean,
  ) => {
    let res = null;
    if (prefetchedTalksBefore.value.length > 0) {
      // prefetch されたデータがあればそれを使う
      res = prefetchedTalksBefore.value;
      prefetchedTalksBefore.value = [];
    } else {
      // prefetch されたデータがなければここで今回分をfetch
      res = await repository.getTalks(
        planId,
        talkRoomId,
        talkId,
        getTalkDateFrom(talkRoomId),
        true,
        false,
        pageSize,
        useCacheIfAvailable,
      );
    }

    // 次回分を非同期で prefetch
    if (ENABLE_PREFETCH && res.length > 0) {
      repository
        .getTalks(
          planId,
          talkRoomId,
          res.at(0)!.talkId,
          getTalkDateFrom(talkRoomId),
          true,
          false,
          pageSize,
          useCacheIfAvailable,
        )
        .then(res => {
          prefetchedTalksBefore.value = res;
        });
    }

    // トークリストに連結
    await updateTalks(planId, res.concat(talks.value));
    // 追加のトークが存在するかどうか (論理削除分は無視)
    hasPrevTalk.value = res.filter(t => t.canView()).length > 0;

    // トーク情報を付与する
    setTalkInfoFromIdList(
      res.map((item: Talk) => {
        return item.talkId;
      }),
    );
  };

  const getTalksAfter = async (
    planId: string,
    talkRoomId: string,
    talkId: string | undefined,
    pageSize = 20,
    useCacheIfAvailable: boolean,
  ) => {
    let res = null;
    if (prefetchedTalksAfter.value.length > 0) {
      // prefetch されたデータがあればそれを使う
      res = prefetchedTalksAfter.value;
      prefetchedTalksAfter.value = [];
    } else {
      // prefetch されたデータがなければここで今回分をfetch
      res = await repository.getTalks(
        planId,
        talkRoomId,
        talkId,
        getTalkDateFrom(talkRoomId),
        false,
        false,
        pageSize,
        useCacheIfAvailable,
      );
    }

    // 次回分を非同期で prefetch
    if (ENABLE_PREFETCH && res.length > 0) {
      repository
        .getTalks(
          planId,
          talkRoomId,
          res.at(-1)!.talkId,
          getTalkDateFrom(talkRoomId),
          false,
          false,
          pageSize,
          useCacheIfAvailable,
        )
        .then(res => {
          prefetchedTalksAfter.value = res;
        });
    }

    // トークリストに連結
    await updateTalks(planId, talks.value.concat(res));
    // 追加のトークが存在するかどうか (論理削除分は無視)
    hasNextTalk.value = res.filter(t => t.canView()).length > 0;

    // トーク情報を付与する
    setTalkInfoFromIdList(
      res.map((item: Talk) => {
        return item.talkId;
      }),
    );
  };

  const getTalksAt = async (
    planId: string,
    talkRoomId: string,
    talkId: string,
    pageSize: number,
    useCacheIfAvailable: boolean,
  ) => {
    // 最初にトークデータ初期化
    resetTalks();

    // talkId 前後のトークを取得して新規に talks へ設定
    const res1 = await repository.getTalks(
      planId,
      talkRoomId,
      talkId,
      getTalkDateFrom(talkRoomId),
      true,
      true,
      pageSize / 2 + 1,
      useCacheIfAvailable,
    );
    const res2 = await repository.getTalks(
      planId,
      talkRoomId,
      talkId,
      getTalkDateFrom(talkRoomId),
      false,
      false,
      pageSize / 2,
      useCacheIfAvailable,
    );

    // トークリストを連結
    const res = res1.concat(res2);
    await updateTalks(planId, res);
    // 追加のトークが存在するかどうか (論理削除分は無視)
    hasPrevTalk.value = res.filter(t => t.canView()).length > 0;
    hasNextTalk.value = res.filter(t => t.canView()).length > 0;

    // トーク情報を付与する
    setTalkInfoFromIdList(
      res.map((item: Talk) => {
        return item.talkId;
      }),
    );
  };

  // 次のトークIDを取得
  const getNextTalkId = async (planId: string, talkRoomId: string, talkId: string) => {
    return await repository.getNextTalkId(planId, talkRoomId, talkId, getTalkDateFrom(talkRoomId));
  };

  const getThreadChildTalks = async (planId: string, talkRoomId: string, threadTalkId: string, latestCount: number) => {
    const childTalks = await repository.getThreadChildTalks(
      planId,
      talkRoomId,
      threadTalkId,
      getTalkDateFrom(talkRoomId),
      latestCount,
    );
    await updateTalkRoomUsers(planId, childTalks);
    return childTalks;
  };

  // 既読取得
  const getLastRead = async (userId: string, talkRoomId: string) => {
    return await repository.getLastRead(userId, talkRoomId);
  };

  // 既読設定
  const setLastRead = async (userId: string, talkRoomId: string, talkId: string) => {
    const result = await repository.setLastRead(userId, talkRoomId, talkId);
    if (result) {
      // ホーム・メンションタブを既読処理
      setTalkRoomUnread(talkRoomId, false);
      talkUnreadStore.setUnread("mention", talkId, false);
    }
    return result;
  };

  // スレッド既読設定
  const setThreadLastRead = async (
    userId: string,
    talkRoomId: string,
    threadTalkId: string,
    talkId: string,
    siblingTalkIds: string[],
  ) => {
    const result = await repository.setThreadLastRead(userId, talkRoomId, threadTalkId, talkId);
    if (result) {
      // スレッド・メンションタブを既読処理
      talkUnreadStore.setUnread("thread", threadTalkId, false);
      talkUnreadStore.setUnread("mention", talkId, false);

      // スレッドを開いたタイミングでスレッド内の兄弟のメンションもすべて既読にする
      for (const siblingTalkId of siblingTalkIds) {
        talkUnreadStore.setUnread("mention", siblingTalkId, false);
      }
    }
    return result;
  };

  const sendTalk = async (
    talkRoomId: string,
    message: string,
    mentionUserIds: string[],
    replyTalkId: string | undefined = undefined,
    threadTalkId: string = "0",
    showInRoom: boolean = true,
  ) => {
    // 返信元が現在のトークリストに存在しない場合はエラー（別チャンネルのトークの可能性あり）
    // https://fincs.backlog.com/view/FINCS-2976#comment-458962486
    if (replyTalkId && !talks.value.some(item => item.talkId === replyTalkId)) {
      throw new Error(`sendTalk: replyTalkId ${replyTalkId} not found in room ${talkRoomId}`);
    }
    // スレッド元が現在のトークリストに存在しない場合はエラー（返信同様）
    if (threadTalkId !== TALK_THREAD_ID_NONE && !talks.value.some(item => item.talkId === threadTalkId)) {
      throw new Error(`sendTalk: threadTalkId ${threadTalkId} not found in room ${talkRoomId}`);
    }
    await repository.sendTalk(talkRoomId, message, mentionUserIds, replyTalkId, threadTalkId, showInRoom);
  };

  const sendFile = async (
    talkRoomId: string,
    file: File,
    note: string,
    mentionUserIds: string[],
    replyTalkId: string | undefined,
    threadTalkId: string = "0",
    showInRoom: boolean = true,
    thumbnailBase64?: string,
  ) => {
    // 返信元が現在のトークリストに存在しない場合はエラー（別チャンネルのトークの可能性あり）
    // https://fincs.backlog.com/view/FINCS-2976#comment-458962486
    if (replyTalkId && !talks.value.some(item => item.talkId === replyTalkId)) {
      throw new Error(`sendFile: replyTalkId ${replyTalkId} not found in room ${talkRoomId}`);
    }
    // スレッド元が現在のトークリストに存在しない場合はエラー（返信同様）
    if (threadTalkId !== TALK_THREAD_ID_NONE && !talks.value.some(item => item.talkId === threadTalkId)) {
      throw new Error(`sendFile: threadTalkId ${threadTalkId} not found in room ${talkRoomId}`);
    }
    await repository.sendFile(
      talkRoomId,
      file,
      note,
      mentionUserIds,
      replyTalkId,
      threadTalkId,
      showInRoom,
      thumbnailBase64,
    );
  };

  const sendFileWithoutRoom = async (file: File, note: string) => {
    return await repository.sendFileWithoutRoom(file, note);
  };

  const editTalk = async (talkId: string, text: string, mentionUserIds: string[], showInRoom: boolean) => {
    await repository.editTalk(talkId, text, mentionUserIds, showInRoom);
  };

  const deleteTalk = async (talkId: string) => {
    await repository.deleteTalk(talkId);
  };

  const sendGood = async (planId: string, talkRoomId: string, talkId: string, userId: string) => {
    await repository.sendGood(planId, talkRoomId, talkId, userId);
  };

  const deleteGood = async (planId: string, talkRoomId: string, talkId: string, userId: string) => {
    await repository.deleteGood(planId, talkRoomId, talkId, userId);
  };

  // トークのコンテンツURLが期限切れであれば再取得する処理
  const checkContentsUrl = async (talk: Talk, skipExpirationCheck: boolean = false): Promise<string | undefined> => {
    try {
      if (!skipExpirationCheck) {
        // 署名付きURLの期限が切れていなければそのまま返す
        const result = CheckS3Expire(talk.contentsUrl);
        if (result) {
          return talk.contentsUrl;
        }
      }
      // コンテンツURLを再取得する
      const res = await repository.getTalkFromApi(selectedTalkRoomId.value, talk.talkId);
      if (res) {
        talk.contentsUrl = res.contents;
        return res.contents;
      }
    } catch (error: any) {
      sentryHandleException(error);
    }
    return undefined;
  };

  // キーを使ってコンテンツURLを取得する
  const getContentsUrl = async (key: string): Promise<{ statusCode: number; presignedUrl: string }> => {
    return await repository.getContentsUrl(key);
  };

  // 以下private

  const addTalkRoomCallback = (observeTalkRoom: ObservedTalkRoom) => {
    // 監視対象ルーム登録
    observeTalkRooms.value.push(observeTalkRoom);
  };

  const changeTalkRoomCallback = async (newObserveRoom: ObservedTalkRoom) => {
    const talkRoomId = newObserveRoom.id;
    const observeRoom = observeTalkRooms.value.find(item => item.id === talkRoomId);
    if (!observeRoom) {
      // 監視対象ルーム未登録時は既読管理しない
      return;
    }
    if (!loginStore.isLogin()) {
      // 未ログイン時は既読管理しない
      return;
    }
    const room = talkRooms.value.find(item => item.talkRoomId === observeRoom.id);
    if (!room) {
      // トークルーム未 fetch 時は既読管理しない
      return;
    }
    if (!canViewTalkRoom(room, planStore.plan!.isOwner, isContractPlan(planStore.plan!))) {
      // トークルームが閲覧不可の場合は既読管理しない
      return;
    }
    if (observeRoom.latesteTalkID === newObserveRoom.latesteTalkID) {
      // latesteTalkIDに違いがなければ何もしない
      return;
    }

    if (!newObserveRoom.latesteTalkID) {
      // 最新トークがなくなった場合、既読決定
      setTalkRoomUnread(talkRoomId, false);
    } else {
      // latesteTalkID の増減によりメッセージ追加 or 削除を判定
      const hasIncreased = !observeRoom.latesteTalkID || observeRoom.latesteTalkID < newObserveRoom.latesteTalkID;
      if (hasIncreased) {
        // メッセージ追加であれば、自分以外の投稿 && 講師に対するDM配信以外であれば未読に設定
        if (
          newObserveRoom.latestUserId !== loginStore.loginInfo!.userId &&
          !(newObserveRoom.latestIsDelivery && planStore.plan!.isOwner)
        ) {
          setTalkRoomUnread(talkRoomId, true);
        }
      } else {
        // メッセージ削除であれば既読トークIDをチェックし、それより大きい場合に未読に設定
        const lastRead = await repository.getLastRead(loginStore.loginInfo!.userId, talkRoomId);
        setTalkRoomUnread(talkRoomId, !lastRead || lastRead < newObserveRoom.latesteTalkID);
      }
    }

    // 最新投稿日時を更新
    setTalkRoomPostDateTime(talkRoomId, newObserveRoom.latestPostDateTime?.toISOString() || "");

    // 値をすべてコピーして保存しておく
    observeRoom.latesteTalkID = newObserveRoom.latesteTalkID;
    observeRoom.latestUserId = newObserveRoom.latestUserId;
    observeRoom.latestPostDateTime = newObserveRoom.latestPostDateTime;
    observeRoom.latestIsDelivery = newObserveRoom.latestIsDelivery;
  };

  const addCallback = async (talk: Talk) => {
    if (talk.talkRoomId !== selectedTalkRoomId.value) {
      // 選択中のトークルームではない場合は何もしない
      return;
    }

    if (!talkRoomSetDone.value) return;

    // 検索結果移動や返信元へのジャンプ後など、最新メッセージを保持していない場合は追加しない
    // (でないと途中に挿入され不正な状態となってしまう)
    if (hasNextTalk.value) {
      return;
    }

    const existingTalk = talks.value.find(item => item.talkId === talk.talkId);
    if (existingTalk) {
      // すでに存在するトークの場合
      if (!existingTalk.canView()) {
        // 以前論理削除済みだった状態からの復旧であれば、トークを更新
        // スレッド内コメントのチャンネル表示 ON->OFF->ON の場合に発生する
        changeCallback(talk);
      }
      return;
    }

    // トークリストにpushする
    await addOneTalk(selectedPlanId.value, talk);
    // トーク情報を付与する。全部awaitで待つと遅いので並列処理とする
    setTalkInfoFromIdList([talk.talkId]);
  };

  const changeCallback = async (talk: Talk) => {
    if (talk.talkRoomId !== selectedTalkRoomId.value) {
      // 選択中のトークルームではない場合は何もしない
      return;
    }

    if (!talkRoomSetDone.value) return;
    const oldIndex = talks.value.findIndex(item => item.talkId === talk.talkId);
    if (oldIndex !== -1) {
      // トークリストの中身を入れ替える
      await updateOneTalk(selectedPlanId.value, talk);
      // トーク情報を付与する。全部awaitで待つと遅いので並列処理とする
      setTalkInfoFromIdList([talk.talkId]);
    }
    // 返信元に変更があった場合
    for (let i = 0; i < talks.value.length; i++) {
      if (talks.value[i].replyTalkId === talk.talkId) {
        // 返信元トークを更新 (OGPや返信は紐づかないので、素のデータのみ設定)
        setReplyTalk(talks.value[i], talk);
      }
    }
    // スレッド元に変更があった場合
    for (let i = 0; i < talks.value.length; i++) {
      if (talks.value[i].threadTalkId === talk.talkId) {
        // スレッド元トークを更新 (OGPや返信は紐づかないので、素のデータのみ設定)
        setThreadTalk(talks.value[i], talk);
      }
    }
  };

  const addUsersCallback = (user: TalkRoomUser) => {
    if (!talkRoomSetDone.value) return;
    talkRoomUsers.value[user.userId] = user;

    // talks, threadTalks 両方更新する
    setUpdateTalkFlag();
    setUpdateThreadTalkFlag();
  };

  const changeUsersCallback = (user: TalkRoomUser) => {
    if (!talkRoomSetDone.value) return;
    talkRoomUsers.value[user.userId] = user;

    // talks, threadTalks 両方更新する
    setUpdateTalkFlag();
    setUpdateThreadTalkFlag();
  };

  // talkに各種情報を付与する
  const setTalkInfoFromIdList = (talkIdList: string[]) => {
    try {
      for (const talkId of talkIdList) {
        const talk = talks.value.find(item => item.talkId === talkId);
        if (talk) {
          // OGP
          setTalkOGPInfo(talk, false);

          // 返信: 参照元があり、未設定の場合のみ
          if (talk.replyTalkId && !talk.replyTalk) {
            setRefTalkInfo(talk, talk.replyTalkId!, replyTalks.value, replyRequestDebounces, setReplyTalk);
          }

          // スレッド: 参照元があり、未設定の場合のみ
          if (talk.isThreadChild() && !talk.threadTalk) {
            setRefTalkInfo(talk, talk.threadTalkId, threadSrcTalks.value, threadRequestDebounces, setThreadTalk);
          }
        }
      }
    } catch (error: any) {
      sentryHandleException(error);
    }

    // ブックマーク情報を付与する（非同期）
    setBookmarkInfoFromIdList(talkIdList, false);

    setUpdateTalkFlag();
  };

  // スレッド内talkに各種情報を付与する
  const setThreadTalkInfo = (talk: Talk) => {
    // OGP
    setTalkOGPInfo(talk, true);

    // 返信・スレッドは付与不要
    // - 返信がスレッド内に含まれることはないため
    // - スレッドの階層化はサポートしないため

    // ブックマーク情報を付与する（非同期）
    setBookmarkInfoFromIdList([talk.talkId], true);

    setUpdateThreadTalkFlag();
  };

  // talkにOGP情報を付与する (talk単位)
  // 即時取得できたら true、取得不要または非同期取得になる場合は false を返す
  const setTalkOGPInfo = (talk: Talk, isThread: boolean): boolean => {
    // テキストメッセージ以外は何もしない（対象外）
    if (talk.messageType !== "text") {
      return false;
    }

    // トーク内のURLを抽出
    const regexpUrl = /(https?|ftp):\/\/[-_.!~*\'()a-zA-Z0-9;\/?:\@&=+\$,%#\u3001-\u30FE\u4E00-\u9FA0\uFF01-\uFFE3]+/g; // eslint-disable-line
    const urlAllMatchesTmp = talk.contents.match(regexpUrl);

    // URLなしの場合は何もしない（設定不要）
    if (!urlAllMatchesTmp) {
      return false;
    }

    // URL の重複を除去し、最初の 3 件のみとする
    // https://arkad-co.slack.com/archives/C06P8P9L95G/p1727078469367379?thread_ts=1727069923.243799&cid=C06P8P9L95G
    const urlAllMatches = _.uniq(urlAllMatchesTmp).slice(0, 3);

    if (USE_CACHE_FOR_ASYNC_DATA) {
      // まずキャッシュから探す
      const ogpList = talkOgpLists.value[talk.talkId];
      if (ogpList) {
        talk.ogpList = ogpList;
        return true;
      }
    }

    // キャッシュになければ非同期で取得
    // 取得中はスクロール待機判定できるようにフラグ立てる
    talk.ogpLoading = true;
    const getOgpListAsync = async () => {
      try {
        let countFromBrowserCache = 0;
        for (let i = 0; i < urlAllMatches.length; i++) {
          if (await setTalkOGPInfoByUrl(talk, urlAllMatches[i], isThread)) {
            countFromBrowserCache++;
          }
        }
        if (countFromBrowserCache < urlAllMatches.length) {
          // 一部でもブラウザキャッシュから取得できなかった場合は debounce 後に取得されるので、
          // その分 ogpLoading を延長してスクロールを継続させる
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } finally {
        talk.ogpLoading = false;
        if (isThread) {
          setUpdateThreadTalkFlag();
        } else {
          setUpdateTalkFlag();
        }
      }
    };
    getOgpListAsync();

    // 非同期処理されるのでここでは false を返す
    return false;
  };

  // talkにOGP情報を付与する (talk内の各url単位)
  // ブラウザキャッシュから取得できたら true、サーバ取得になる場合は false を返す
  const setTalkOGPInfoByUrl = async (talk: Talk, url: string, isThread: boolean) => {
    await delayAsyncForDebug();

    // まず API から OGP を取得する関数を定義
    const getAndSetOgpToTalk = async (talk: Talk, url: string) => {
      try {
        const ogpLists = await ogpRepository.getOgp(url);
        // OGP取れなかったら作らない
        if (ogpLists?.length) {
          const ogp = new TalkOgp("", "", "", url);
          ogp.title = _.find(ogpLists, ["property", "og:title"])?.content;
          ogp.description = _.find(ogpLists, ["property", "og:description"])?.content;
          ogp.image = _.find(ogpLists, ["property", "og:image"])?.content;
          talk.ogpList.push(ogp);
          talkOgpLists.value[talk.talkId] = talk.ogpList; // キャッシュ登録

          // 表示更新
          if (isThread) {
            setUpdateThreadTalkFlag();
          } else {
            setUpdateTalkFlag();
          }
        }
        // ブラウザキャッシュ済みであることを記録
        ogpRequestCached.set(url, true);
      } catch (_e) {
        // ogp取得APIエラー
        // ogpRepository.getOgp 配下で sentry にログを送っているのでここでは何もしない
      }
    };

    if (USE_CACHE_FOR_ASYNC_DATA) {
      const cachedOgp = ogpRequestCached.get(url);
      if (cachedOgp) {
        // OGP URL がブラウザキャッシュにあれば即時取得
        await getAndSetOgpToTalk(talk, url);
        return true;
      }
    }

    // OGP URL がブラウザキャッシュになければデバウンスでバースト抑止
    // 200msタイマーとしたのは、OGP取得リクエストが概ね100ms前後なので余裕を持ちつつ、表示が遅くなりすぎない値として選定
    const key = `${talk.talkId}-${url}`;
    let ogpRequestDebounce = ogpRequestDebounces.get(key);
    if (!ogpRequestDebounce) {
      ogpRequestDebounce = _.debounce(() => {
        getAndSetOgpToTalk(talk, url);
      }, 200);
      ogpRequestDebounces.set(key, ogpRequestDebounce);
    }
    ogpRequestDebounce();

    // debounce で非同期処理されるのでここでは false を返す
    return false;
  };

  // talkに参照情報（返信・スレッド）を付与する
  // 即時取得できたら true、取得不要または非同期取得になる場合は false を返す
  const setRefTalkInfo = (
    talk: Talk,
    refTalkId: string,
    refTalksCache: Talk[],
    refRequestDebounces: Map<any, any>,
    setRefTalk: (talk: Talk, refTalk: Talk) => void,
  ): boolean => {
    // 返信情報を loading で初期化。後続の処理で設定されるまではこれを表示して初期レイアウト調整
    setRefTalk(talk, newLoadingTalk(refTalkId));

    if (USE_CACHE_FOR_ASYNC_DATA) {
      // まずキャッシュから探す
      let refTalk = refTalksCache.find(item => item.talkId === refTalkId);
      if (refTalk) {
        setRefTalk(talk, refTalk);
        return true;
      }

      // 次に自分のトークリストから探す
      refTalk = talks.value.find(item => item.talkId === refTalkId);
      if (refTalk) {
        refTalksCache.push(refTalk); // キャッシュ登録
        setRefTalk(talk, refTalk);
        return true;
      }
    }

    // 上記いずれもなければFirestoreから取得しキャッシュに追加する
    // デバウンスでバースト抑止
    // 200msタイマーとしたのは、トーク取得リクエストが概ね100ms前後なので余裕を持ちつつ、表示が遅くなりすぎない値として選定
    const key = talk.talkId;
    let refRequestDebounce = refRequestDebounces.get(key);
    if (!refRequestDebounce) {
      refRequestDebounce = _.debounce(async () => {
        await delayAsyncForDebug();
        const res = await repository.getTalk(selectedPlanId.value, selectedTalkRoomId.value, refTalkId);
        if (res) {
          if (!refTalksCache.some(item => item.talkId === refTalkId)) {
            refTalksCache.push(res); // キャッシュ登録。非同期に取るので重複チェックもしておく
          }
          setRefTalk(talk, res);

          // このルートだけ非同期で処理されるのでここで update フラグを立てる
          // 返信元・スレッド元トークはスレッド内に含まれないので、setUpdateThreadTalkFlag は不要
          setUpdateTalkFlag();
        }
      }, 200);
      refRequestDebounces.set(key, refRequestDebounce);
    }
    refRequestDebounce();

    // debounce で非同期処理されるのでここでは false を返す
    return false;
  };

  const setReplyTalk = (talk: Talk, replyTalk: Talk) => {
    if (replyTalk.userId) {
      // 返信元ユーザーが未設定の場合に非同期で取得
      fetchTalkRoomUser(replyTalk);
    }

    // 有効チェック
    replyTalk.talkRoomId = talk.talkRoomId;
    validateTalk(replyTalk);

    // 返信元トークを設定
    talk.replyTalk = replyTalk;
  };

  const setThreadTalk = (talk: Talk, threadTalk: Talk) => {
    if (threadTalk.userId) {
      // スレッド元ユーザーが未設定の場合に非同期で取得
      fetchTalkRoomUser(threadTalk);
    }

    // 有効チェック
    threadTalk.talkRoomId = talk.talkRoomId;
    validateTalk(threadTalk);

    // スレッド元トークを設定
    talk.threadTalk = threadTalk;
  };

  const setBookmarkInfoFromIdList = async (targetTalkIds: string[], isThread: boolean) => {
    if (!loginStore.loginInfo.userId) {
      // 未ログインではブックマーク情報を取得できないので何もしない
      return;
    }

    // 現在のルームにおけるブックマーク情報を取得、targetTalks と合致するものに絞り込む
    const keys = await repository.getTalkBookmarkKeysByRoomId(
      selectedPlanId.value,
      loginStore.loginInfo.userId,
      selectedTalkRoomId.value,
    );
    const filteredKeys = keys.filter(k => targetTalkIds.includes(k.talkId));

    // 読み込み済みトークにブックマーク情報を付与して表示更新
    const loadedTalks = isThread ? threadTalks : talks;
    let needsUpdate = false;
    for (const key of filteredKeys) {
      const talk = loadedTalks.value.find(t => t.talkId === key.talkId);
      if (talk && !talk.bookmarked) {
        talk.bookmarked = true;
        needsUpdate = true;
      }
    }
    if (needsUpdate) {
      if (isThread) {
        setUpdateThreadTalkFlag();
      } else {
        setUpdateTalkFlag();
      }
    }
  };

  // トークルーム数が最大値に達しているかどうか
  const isMaxGroupTalkRooms = () => {
    return talkRooms.value.filter(room => !room.isArchive && !room.isDirectMessage).length >= TALK_ROOM_GROUP_MAX;
  };

  // DMトークルーム数が最大値に達しているかどうか
  const isMaxDMTalkRooms = () => {
    return talkRooms.value.filter(room => room.isDirectMessage && !room.isArchive).length >= TALK_ROOM_DM_MAX;
  };

  return {
    talkRooms,
    talkRoomsForSetting,
    talks,
    talkRoomSetDone,
    hasPrevTalk,
    hasNextTalk,
    loading,
    updateTalk,
    updateThreadTalk,
    replyTalkToEdit,
    threadTalks,
    talkRoomUsers,
    userGoodList,
    validateTalk,
    startTalkRoomsObserve,
    stopTalkRoomsObserve,
    startUsersObserve,
    stopUsersObserve,
    startTalksObserve,
    stopTalksObserve,
    startThreadTalksObserve,
    stopThreadTalksObserve,
    hasUnreadTalk,
    getTalkRooms,
    getTalkRoomsForSetting,
    getTalkRoomDetail,
    getGoodUsers,
    updateTalkRoomUsers,
    getMentionUsers,
    searchTalkCountsInPlan,
    searchTalkIds,
    hasTalk,
    getTalksBefore,
    getTalksAfter,
    getTalksAt,
    getThreadChildTalks,
    getNextTalkId,
    getLastRead,
    setLastRead,
    setThreadLastRead,
    sendTalk,
    setTalkOGPInfo,
    getContentsUrl,
    sendFile,
    sendFileWithoutRoom,
    initPlan,
    initTalkRoom,
    editTalk,
    deleteTalk,
    sendGood,
    deleteGood,
    clearUpdateTalkFlag,
    clearUpdateThreadTalkFlag,
    updateTalkValue,
    checkContentsUrl,

    startUserGoodsObserve,
    stopUserGoodsObserve,
    isMaxGroupTalkRooms,
    isMaxDMTalkRooms,

    // 以下は VisibleForTesting
    selectedTalkRoomId,
    getTalkDateFrom,
    fetchTalkRoomUser,
    addTalkRoomCallback,
    changeTalkRoomCallback,
    changeCallback,
    setRefTalkInfo,
    addThreadCallback,
    changeThreadCallback,
    setReplyTalk,
    setThreadTalk,
  };
});
